// A base view that binds its state to the state of a model prop passed in.
// Makes updates based on external backbone models/collections :ok_hand:
// state.model is a JSON representation of the model; props.json is the
// original backbone model. Use state when possible in render(), since json
// access is faster.

import { Component } from 'react';
import { PropTypes as T } from 'prop-types';
import { isEqual } from 'lodash/lang';

import { Model } from 'backbone';

export const BACKBONE_EVENTS = [
  'add',
  'remove',
  'update',
  'sort',
  'change',
  'sync',
  'error',
];

export const BIND_DEFAULT_OPTIONS = {
  nestedModels: BACKBONE_EVENTS,
};

// Ignore the unused `model` prop in state, since we're really only using it
// to determine if there was an update that requires a re-render.
/* eslint react/no-unused-state: 0 */

export default class BackboneBoundView extends Component {
  static propTypes = {
    model: T.instanceOf(Model).isRequired,
  }

  static events = BACKBONE_EVENTS;

  constructor(props) {
    super(props);

    const { model } = props;

    // Use the model as the source of truth, instead of manually calling
    // renders on add/remove. This allows all components that use the same
    // model instance to update all components using it.
    this.bindBackboneEvents(
      model,
      this.constructor.events,
      this.updateModel,
      { nestedModels: BACKBONE_EVENTS },
    );

    model.on('request', () => this.setSavingState(true));
    model.on('sync', () => this.setSavingState(false));
    model.on('error', () => this.setSavingState(false));

    // Use state, because backbone models mutate themselves. This allows us to
    // smartly re-render only changed elements.
    this.state = {
      model: model.toJSON(),
      saving: false,
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);
  }

  setSavingState(isSaving = false) {
    this.setState({
      saving: isSaving,
    });
  }

  bindBackboneEvents(model, events, fn, options = BIND_DEFAULT_OPTIONS) {
    events.forEach(e => {
      model.on(e, fn.bind(this, e));

      // Recursively bind nested objects if asked to (default is true.)
      if (options.nestedModels) {
        Object.keys(model.nestedModels || []).forEach(k => {
          this.bindBackboneEvents(model.get(k), options.nestedModels, fn, options);
        });
      }
    });
  }

  updateModel = () => {
    this.setState({
      model: this.props.model.toJSON(),
    });
  }
}



// WEBPACK FOOTER //
// ./app/components/model_forms/base.jsx